import { Button } from "@/components/ui/baseUi/button"
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/baseUi/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { type ColumnDef, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { useState } from "react"

type HUDatatableProps<T> = {
  data: T[]
  columns: ColumnDef<T, any>[]
  totalElements?: number
  pageSize?: number
  setPageSize?: (size: number) => void
  handlePreviousPage?: () => void
  handleNextPage?: () => void
  hasPreviousPage?: boolean
  hasNextPage?: boolean
  selectedRow?: T
  handleRowSelectionChange?: (row: T) => void
  selectedRowIndex?: number
  isLoading?: boolean
  hideHeader?: boolean
  hideFooter?: boolean
} & React.ComponentProps<"div">

export function HUDatatable<T>({
  data,
  columns,
  totalElements,
  pageSize,
  setPageSize,
  handlePreviousPage,
  handleNextPage,
  hasPreviousPage,
  hasNextPage,
  handleRowSelectionChange,
  selectedRowIndex,
  isLoading,
  hideHeader = false,
  hideFooter = false,
  className,
  ...props
}: HUDatatableProps<T>) {
  const [open, setOpen] = useState(false)

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const sizeOptions = [
    { value: 20, label: "20" },
    { value: 30, label: "30" },
    { value: 50, label: "50" },
  ]

  const showFooter =
    !hideFooter &&
    !isLoading &&
    (!totalElements || (totalElements && sizeOptions[0] && totalElements >= sizeOptions[0].value))

  return (
    <div className={cn("p-datatable p-component p-datatable-selectable", className)} {...props}>
      <table className="p-datatable-table w-full">
        {!hideHeader && (
          <thead className="p-datatable-thead-new">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers
                  .filter((header) => !header.column.columnDef.meta?.hidden)
                  .map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.headerClassName ?? ""}
                      colSpan={header.colSpan}
                    >
                      <div className="p-column-header-content">
                        <Text fontStyle="LL">
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </Text>
                      </div>
                    </th>
                  ))}
              </tr>
            ))}
          </thead>
        )}

        <tbody className="p-datatable-tbody">
          {table.getRowModel().rows.map((row, index) => (
            <tr
              key={row.id}
              className={`${index === selectedRowIndex ? "p-highlight" : ""} p-selectable-row`}
              onClick={() => handleRowSelectionChange?.(row.original)}
            >
              {row
                .getVisibleCells()
                .filter((cell) => !cell.column.columnDef.meta?.hidden)
                .map((cell) => (
                  <td
                    key={cell.id}
                    className={cell.column.columnDef.meta?.className ?? ""}
                    style={cell.column.columnDef.meta?.style}
                    onClick={cell.column.columnDef.meta?.onClick}
                  >
                    <Text fontStyle="BM">{flexRender(cell.column.columnDef.cell, cell.getContext())}</Text>
                  </td>
                ))}
            </tr>
          ))}
        </tbody>

        {showFooter && (
          <tfoot className="p-datatable-footer">
            <tr>
              <td colSpan={columns.length}>
                <div className="mt-4 flex flex-row items-center justify-center gap-4">
                  <div className="flex flex-row gap-2">
                    <Button variant="outline" size="sm" onClick={handlePreviousPage} disabled={!hasPreviousPage}>
                      {word("global.previous")}
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleNextPage} disabled={!hasNextPage}>
                      {word("global.next")}
                    </Button>
                  </div>
                  <div className="flex bg-neutral-200" style={{ width: 1, height: 42 }} />
                  <div className="flex flex-row items-center justify-center gap-2">
                    <Text fontStyle="LM" className="text-textDefault-caption">
                      {word("finances.page.table.footer.label")}
                    </Text>
                    <Popover open={open} onOpenChange={setOpen}>
                      <PopoverTrigger
                        render={
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            className="h-11 w-20 justify-between"
                          >
                            {pageSize?.toString()}
                            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        }
                      />
                      <PopoverContent className="w-20 border-0 p-0" align="start">
                        <Command>
                          <CommandList>
                            <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                            <CommandGroup>
                              {sizeOptions.map((sizeOption) => (
                                <CommandItem
                                  key={sizeOption.value}
                                  value={sizeOption.label}
                                  onSelect={(currentValue) => {
                                    setPageSize?.(parseInt(currentValue))
                                    setOpen(false)
                                  }}
                                  className="cursor-pointer hover:bg-neutral-200"
                                >
                                  <CheckIcon
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      pageSize === sizeOption.value ? "opacity-100" : "opacity-0",
                                    )}
                                  />
                                  {sizeOption.label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}
